<template>
  <div class="material-lot-list">
    <div class="page-header">
      <h2>자재 LOT 관리</h2>
      <div class="header-actions">
        <el-button
          v-if="userStore.isMaterialOrderManager"
          type="primary"
          @click="openCreateDialog"
        >
          <el-icon><Plus /></el-icon>
          신규 LOT
        </el-button>
        <el-button @click="showHelp">
          <el-icon><QuestionFilled /></el-icon>
          도움말
        </el-button>
      </div>
    </div>

    <!-- 필터 영역 -->
    <el-card class="filter-card">
      <el-form :inline="true" :model="filters">
        <el-form-item label="법인">
          <el-select v-model="filters.company" clearable placeholder="전체">
            <el-option v-for="c in codeStore.ourCompanys" :key="c.id" :label="c.name" :value="c.id" />
          </el-select>
        </el-form-item>
        <el-form-item label="자재유형">
          <el-select v-model="filters.materialType" clearable placeholder="전체">
            <el-option label="다운" value="DOWN" />
            <el-option label="원단" value="FABRIC" />
            <el-option label="부자재" value="ACCESSORY" />
            <el-option label="기타" value="OTHER" />
          </el-select>
        </el-form-item>
        <el-form-item label="상태">
          <el-select v-model="filters.status" clearable placeholder="전체">
            <el-option label="발주" value="ORDERED" />
            <el-option label="입고" value="RECEIVED" />
            <el-option label="배분완료" value="ALLOCATED" />
            <el-option label="소진" value="DEPLETED" />
          </el-select>
        </el-form-item>
        <el-form-item label="보관창고">
          <el-select v-model="filters.warehouse" clearable placeholder="전체">
            <el-option
              v-for="wh in warehouses"
              :key="wh.id"
              :label="`${wh.warehouse_type_display} - ${wh.name}`"
              :value="wh.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="잔여수량 있음">
          <el-switch v-model="filters.available" />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="fetchData">검색</el-button>
          <el-button @click="resetFilters">초기화</el-button>
          <el-button :type="showAll ? '' : 'warning'" @click="toggleShowAll" size="small">
            {{ showAll ? '스마트뷰' : '전체보기' }}
          </el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 데이터 테이블 -->
    <el-card class="table-card">
      <el-table :data="lots || []" v-loading="loading" stripe>
        <el-table-column prop="lot_number" label="LOT번호" width="150" />
        <el-table-column prop="company_name" label="법인" width="100" />
        <el-table-column prop="material_name" label="자재명" width="200" />
        <el-table-column label="유형" width="80">
          <template #default="{ row }">
            <el-tag size="small">{{ row.material_type_display }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="상태" width="80">
          <template #default="{ row }">
            <el-tag :type="getStatusType(row.status)" size="small">
              {{ row.status_display }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="quantity" label="총수량" width="100" align="right">
          <template #default="{ row }">
            {{ formatNumber(row.quantity) }} {{ row.unit }}
          </template>
        </el-table-column>
        <el-table-column prop="remaining_quantity" label="잔여수량" width="100" align="right">
          <template #default="{ row }">
            {{ formatNumber(row.remaining_quantity) }} {{ row.unit }}
          </template>
        </el-table-column>
        <el-table-column label="사용률" width="100" align="center">
          <template #default="{ row }">
            <el-progress
              :percentage="row.usage_percentage || 0"
              :stroke-width="8"
              :show-text="false"
            />
            <span class="usage-text">{{ (row.usage_percentage || 0).toFixed(0) }}%</span>
          </template>
        </el-table-column>
        <el-table-column v-if="userStore.hasCostAccess" label="단가" width="100" align="right">
          <template #default="{ row }">
            {{ formatCurrency(row.unit_cost) }}
          </template>
        </el-table-column>
        <el-table-column prop="supplier_name" label="공급처" width="120" />
        <el-table-column prop="warehouse_name" label="보관창고" width="120" />
        <el-table-column prop="order_date" label="발주일" width="110" />
        <el-table-column label="작업" width="150" fixed="right">
          <template #default="{ row }">
            <el-button
              v-if="userStore.isMaterialOrderManager && row.remaining_quantity > 0"
              size="small"
              type="primary"
              @click="openAllocateDialog(row)"
            >
              배분
            </el-button>
            <el-button size="small" @click="openDetailDialog(row)">상세</el-button>
          </template>
        </el-table-column>
      </el-table>

      <el-pagination
        v-model:current-page="pagination.page"
        v-model:page-size="pagination.pageSize"
        :page-sizes="[20, 50, 100]"
        :total="pagination.total"
        layout="total, sizes, prev, pager, next"
        @size-change="fetchData"
        @current-change="fetchData"
      />
    </el-card>

    <!-- 생성 다이얼로그 -->
    <MaterialLotFormDialog
      v-model="showCreateDialog"
      @saved="handleSaved"
    />

    <!-- 배분 다이얼로그 -->
    <MaterialLotAllocateDialog
      v-model="showAllocateDialog"
      :lot="selectedLot"
      @saved="handleAllocated"
    />

    <!-- 상세 다이얼로그 -->
    <MaterialLotDetailDialog
      v-model="showDetailDialog"
      :lot="selectedLot"
    />

    <!-- 도움말 패널 -->
    <HelpPanel
      v-model="helpVisible"
      :content-path="'screens/material-lot-list'"
    />
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { useUserStore } from '@/stores/user'
import { Plus, QuestionFilled } from '@element-plus/icons-vue'
import axiosInstance from '@/api/axios'
import { useCodeStore } from '@/stores/codeStore'
import MaterialLotFormDialog from '@/components/costing/MaterialLotFormDialog.vue'
import MaterialLotAllocateDialog from '@/components/costing/MaterialLotAllocateDialog.vue'
import MaterialLotDetailDialog from '@/components/costing/MaterialLotDetailDialog.vue'
import HelpPanel from '@/components/help/HelpPanel.vue'

const userStore = useUserStore()
const codeStore = useCodeStore()

const loading = ref(false)
const lots = ref([])
const selectedLot = ref(null)
const helpVisible = ref(false)
const showCreateDialog = ref(false)
const showAllocateDialog = ref(false)
const showDetailDialog = ref(false)
const warehouses = ref([])
const showAll = ref(false)

const filters = reactive({
  company: null,
  materialType: null,
  status: null,
  warehouse: null,
  available: false
})

const pagination = reactive({
  page: 1,
  pageSize: 20,
  total: 0
})

const getStatusType = (status) => {
  const types = {
    'ORDERED': 'warning',
    'RECEIVED': '',
    'ALLOCATED': 'success',
    'DEPLETED': 'info'
  }
  return types[status] || 'info'
}

const formatNumber = (num) => {
  return num?.toLocaleString() || '0'
}

const formatCurrency = (amount) => {
  return amount ? `$${Number(amount).toFixed(2)}` : '-'
}

const fetchData = async () => {
  loading.value = true
  try {
    const params = {
      page: pagination.page,
      page_size: pagination.pageSize,
    }
    if (filters.company) params.company = filters.company
    if (filters.materialType) params.material_type = filters.materialType
    if (filters.status) params.status = filters.status
    if (filters.warehouse) params.warehouse = filters.warehouse
    if (filters.available) params.available = 'true'

    // Remove empty filters
    Object.keys(params).forEach((key) => {
      if (!params[key]) {
        delete params[key]
      }
    })

    // Smart filter: exclude DEPLETED when showAll is false
    if (!showAll.value && !params.status) {
      params.status__in = ['ORDERED', 'RECEIVED', 'ALLOCATED']
    }

    const response = await axiosInstance.get('material-lots/', { params })
    lots.value = response.data.results || response.data
    pagination.total = response.data.count || lots.value.length
  } catch (error) {
    console.error('Failed to fetch material lots:', error)
  } finally {
    loading.value = false
  }
}

const resetFilters = () => {
  filters.company = null
  filters.materialType = null
  filters.status = null
  filters.warehouse = null
  filters.available = false
  pagination.page = 1
  showAll.value = false
  fetchData()
}

const toggleShowAll = () => {
  showAll.value = !showAll.value
  pagination.page = 1
  fetchData()
}

const fetchWarehouses = async () => {
  try {
    const response = await axiosInstance.get('warehouses/', { params: { is_active: true } })
    warehouses.value = response.data.results || response.data
  } catch (error) {
    console.error('Failed to fetch warehouses:', error)
  }
}

const openCreateDialog = () => {
  showCreateDialog.value = true
}

const openAllocateDialog = (lot) => {
  selectedLot.value = lot
  showAllocateDialog.value = true
}

const openDetailDialog = (lot) => {
  selectedLot.value = lot
  showDetailDialog.value = true
}

const handleSaved = () => {
  showCreateDialog.value = false
  fetchData()
}

const handleAllocated = () => {
  showAllocateDialog.value = false
  fetchData()
}

const showHelp = () => {
  helpVisible.value = true
}

onMounted(() => {
  fetchData()
  fetchWarehouses()
  codeStore.fetchOurCompanys()
})
</script>

<style scoped>
.material-lot-list {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-header h2 {
  margin: 0;
}

.header-actions {
  display: flex;
  gap: 10px;
}

.filter-card {
  margin-bottom: 20px;
}

.table-card {
  margin-bottom: 20px;
}

.usage-text {
  font-size: 11px;
  color: #909399;
  margin-left: 5px;
}

.el-pagination {
  margin-top: 20px;
  justify-content: flex-end;
}
</style>
